// The plain form for an activity: used by "Fill it in yourself" and by Edit. Saves directly.
import { useRef, useState, type FormEvent } from 'react';
import { ACTIVITY_STATUSES, ACTIVITY_TYPES, ActivityCreate, STATUS_LABELS, TYPE_LABELS, type ActivityDetail, type ActivityStatus, type ActivityType, type SessionUser, type SpaceDetail } from '@dpw/shared';
import { localDay, localTime, toIsoAt } from '../lib/format';
import { IconDown, IconUp, IconX } from './icons';
import { ErrorNote, Toggle } from './bits';
import { parseRef, peopleOptions, refValue, teamOf } from './people';
import { useSpaceActivities } from '../lib/queries';

export interface ActivityFormValue {
  input: ActivityCreate;
  /** Edit only: the fields that differ from what was loaded. Save sends just these. */
  changed: Partial<ActivityCreate>;
  files: File[];
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export function ActivityForm({ space, me, initial, defaultTrackId, defaultParentId, submitLabel, busy, error, onSubmit, onCancel }: {
  space: SpaceDetail; me: SessionUser; initial?: ActivityDetail; defaultTrackId?: string | undefined; submitLabel: string; busy: boolean; error: unknown;
  /** A new sub-activity: the activity it is part of. */
  defaultParentId?: string | undefined;
  onSubmit: (v: ActivityFormValue) => void; onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [type, setType] = useState<ActivityType>(initial?.type ?? 'task');
  const [trackId, setTrackId] = useState(initial?.trackId ?? defaultTrackId ?? space.tracks[0]?.id ?? '');
  const [status, setStatus] = useState<ActivityStatus>(initial?.status ?? 'not_started');
  const [owners, setOwners] = useState<string[]>(initial ? initial.owners.map(o => o.id) : [me.id]);
  const [date, setDate] = useState(initial?.startsAt ? localDay(initial.startsAt) : '');
  const [start, setStart] = useState(initial?.startsAt ? localTime(initial.startsAt) : '10:00');
  const [end, setEnd] = useState(initial?.endsAt ? localTime(initial.endsAt) : '11:00');
  const [location, setLocation] = useState(initial?.location ?? '');
  const [dueDate, setDueDate] = useState(initial?.dueDate ?? '');
  const [dueTime, setDueTime] = useState(initial?.dueTime ?? '');
  const [milestone, setMilestone] = useState(initial?.isMilestone ?? false);
  const [needsPrep, setNeedsPrep] = useState(initial?.needsPrep ?? false);
  const [requestedBy, setRequestedBy] = useState(refValue(initial?.requestedBy));
  const [waitingOn, setWaitingOn] = useState(refValue(initial?.waitingOn));
  const [description, setDescription] = useState(initial?.description ?? '');
  const [tags, setTags] = useState((initial?.tags ?? []).join(' '));
  const [steps, setSteps] = useState<string[]>([]);
  const [stepDraft, setStepDraft] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState('');
  // Part of: optional, any activity in this space (any depth). Not itself; the server refuses a loop.
  const [parentId, setParentId] = useState(initial ? initial.parentActivityId ?? '' : defaultParentId ?? '');
  const acts = useSpaceActivities(space.id, { includeDone: true });
  const parents = (acts.data ?? []).filter(x => x.id !== initial?.id);

  const timed = type === 'meeting' || type === 'workshop';
  const team = teamOf(space, me);
  const people = peopleOptions(space, me);

  const build = (): ActivityCreate => ({
      trackId, type, title: title.trim(), description: description.trim() || null, status, ownerIds: owners,
      location: timed ? location.trim() || null : null,
      startsAt: timed && date ? toIsoAt(date, start) : null,
      endsAt: timed && date && end ? toIsoAt(date, end) : null,
      dueDate: dueDate || null, dueTime: dueDate && dueTime ? dueTime : null,
      isMilestone: milestone, needsPrep, requestedBy: parseRef(requestedBy), waitingOn: parseRef(waitingOn),
      tags: tags.split(/[\s,]+/).map(t => t.replace(/^#/, '')).filter(Boolean),
      parentActivityId: parentId || null,
      ...(initial ? {} : { steps: steps.map(s => ({ title: s })) }),
  });
  // What the form looked like when it opened, to send only what was changed.
  const baseline = useRef<ActivityCreate | null>(null);
  baseline.current ??= build();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem('');
    const input = build();
    const check = ActivityCreate.safeParse(input);
    if (!check.success) { setProblem(check.error.issues.map(i => `${i.path.join('.') || 'Form'}: ${i.message}`).join('. ')); return; }
    const before = baseline.current!;
    const changed = Object.fromEntries(Object.entries(input).filter(([k, v]) => k !== 'steps' && !same(v, before[k as keyof ActivityCreate]))) as Partial<ActivityCreate>;
    onSubmit({ input: check.data, changed, files });
  };
  const move = (i: number, d: number) => setSteps(s => { const n = [...s]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x!); return n; });

  return (
    <form onSubmit={submit}>
      <label className="fld">Title<input className="inp" value={title} onChange={e => setTitle(e.target.value)} required maxLength={200} autoFocus placeholder="What is it?" /></label>
      <div className="fld">Type
        <div className="seg2" role="radiogroup" aria-label="Type">
          {ACTIVITY_TYPES.map(t => <button type="button" key={t} className={type === t ? 'on' : ''} onClick={() => setType(t)} aria-pressed={type === t}>{TYPE_LABELS[t]}</button>)}
        </div>
      </div>
      <div className="formgrid">
        <label className="fld">Space and track
          <select className="inp" value={trackId} onChange={e => setTrackId(e.target.value)}>
            {space.tracks.map(t => <option key={t.id} value={t.id}>{space.name} · {t.name}</option>)}
          </select>
        </label>
        <label className="fld">Status
          <select className="inp" value={status} onChange={e => setStatus(e.target.value as ActivityStatus)}>
            {ACTIVITY_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </label>
      </div>
      <label className="fld">Part of (optional)
        <select className="inp" value={parentId} onChange={e => setParentId(e.target.value)}>
          <option value="">Not part of another activity</option>
          {parentId && !parents.some(x => x.id === parentId) && <option value={parentId}>{initial?.partOf?.title ?? 'The activity it was part of'}</option>}
          {parents.map(x => <option key={x.id} value={x.id}>{x.title} ({x.trackName})</option>)}
        </select>
      </label>
      <div className="fld">Owners (one or more)
        <div className="chipsel">
          {team.map(m => {
            const on = owners.includes(m.id);
            return <button type="button" key={m.id} className={on ? 'on' : ''} aria-pressed={on}
              onClick={() => setOwners(o => on ? o.filter(x => x !== m.id) : [...o, m.id])}>{m.name}</button>;
          })}
        </div>
      </div>
      {timed && (
        <div className="formrow">
          <label className="fld">Date<input className="inp" type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
          <label className="fld">Starts<input className="inp" type="time" value={start} onChange={e => setStart(e.target.value)} /></label>
          <label className="fld">Ends<input className="inp" type="time" value={end} onChange={e => setEnd(e.target.value)} /></label>
          <label className="fld">Where<input className="inp" value={location} onChange={e => setLocation(e.target.value)} placeholder="Room or link" /></label>
        </div>
      )}
      <div className="formrow">
        <label className="fld">Due date{timed ? ' (optional)' : ''}<input className="inp" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /></label>
        <label className="fld">Due time (optional)<input className="inp" type="time" value={dueTime} onChange={e => setDueTime(e.target.value)} disabled={!dueDate} /></label>
      </div>
      <div className="formrow">
        <div className="togrow"><Toggle on={milestone} onChange={setMilestone} label="Milestone" /><span>Milestone <span className="xs mut">(sign-offs, submissions, go-lives)</span></span></div>
        <div className="togrow"><Toggle on={needsPrep} onChange={setNeedsPrep} label="Needs prep" /><span>Needs prep</span></div>
      </div>
      <div className="formgrid">
        <label className="fld">Requested by
          <select className="inp" value={requestedBy} onChange={e => setRequestedBy(e.target.value)}>
            <option value="">Nobody in particular</option>
            {people.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label className="fld">Waiting on
          <select className="inp" value={waitingOn} onChange={e => setWaitingOn(e.target.value)}>
            <option value="">Nobody</option>
            {people.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
      </div>
      <label className="fld">Notes<textarea className="inp" value={description} onChange={e => setDescription(e.target.value)} rows={3} /></label>
      <label className="fld">Tags<input className="inp" value={tags} onChange={e => setTags(e.target.value)} placeholder="steering policy" /></label>
      {!initial && (
        <div className="fld">Steps, in order
          <div className="panel" style={{ padding: '6px 12px' }}>
            {steps.map((s, i) => (
              <div className="steprow" key={`${i}-${s}`}>
                <span className="n">{i + 1}</span><span className="t">{s}</span>
                <span className="mv">
                  <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}><IconUp /></button>
                  <button type="button" aria-label="Move down" disabled={i === steps.length - 1} onClick={() => move(i, 1)}><IconDown /></button>
                  <button type="button" aria-label="Remove step" onClick={() => setSteps(x => x.filter((_, j) => j !== i))}><IconX /></button>
                </span>
              </div>
            ))}
            <div className="rowc" style={{ padding: '6px 0' }}>
              <input className="inp" value={stepDraft} onChange={e => setStepDraft(e.target.value)} placeholder="Add a step and press Enter"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = stepDraft.trim(); if (v) { setSteps(s => [...s, v]); setStepDraft(''); } } }} />
            </div>
          </div>
        </div>
      )}
      {!initial && (
        <div className="fld">Files
          <input className="inp" type="file" multiple onChange={e => setFiles(Array.from(e.target.files ?? []))} style={{ paddingTop: 8 }} />
          <span className="hint">They go on the activity and in the track Files.</span>
        </div>
      )}
      {problem ? <div className="err" role="alert">{problem}</div> : error ? <ErrorNote error={error} /> : null}
      <div className="rowc">
        <button className="bt pri" disabled={busy}>{busy ? <span className="spin" /> : null}{submitLabel}</button>
        <button type="button" className="bt" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
